import DashboardLayout from "../components/DashboardLayout";
import { ApBuilderIframe } from "../components/ApBuilderIframe";
import { FlowStepIcons } from "../components/FlowStepIcons";
import { ExploreTemplates } from "../components/ExploreTemplates";
import Omniflow from "../components/OmniflowLazy";
import { useEffect, useState, useMemo, useRef } from "react";
import { getKeycloakToken, updateKeycloakToken } from "../services/keycloak";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useFlowRuns } from "../hooks/useFlowRuns";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  alpha,
  Card,
  CardContent,
  Divider,
  Chip,
  Skeleton,
  Menu,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  RestartAlt as ResetIcon,
  History as HistoryIcon,
  AccountTree as FlowsIcon,
  Storefront as ExploreIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { useOrganization } from "../contexts/OrganizationContext";
import apiService, { flowsApi, projectsApi } from "../services/rag-service";
import { toast } from "sonner";
import { useThemeMode } from "../contexts/ThemeContext";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const lang = i18n.language || "en-US";
  return new Date(dateString).toLocaleString(lang === "ar" ? "ar-SA" : "en-US");
};
const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "enabled":
      return "success";
    case "disabled":
      return "default";
    case "pending":
      return "warning";
    case "error":
      return "error";
    default:
      return "default";
  }
};

// Single source of truth for every real FlowRunStatus this UI exposes: its
// filter-dropdown label key and its status-chip color. QUOTA_EXCEEDED and
// LOG_SIZE_EXCEEDED are deliberately excluded - billing/internal-only
// concepts this app has no other UI for, so they'd be dead filter options.
const RUN_STATUS_CONFIG = [
  { value: "succeeded", labelKey: "flows.succeeded", color: "success" },
  { value: "failed", labelKey: "flows.failedStatus", color: "error" },
  { value: "running", labelKey: "flows.runningStatus", color: "warning" },
  { value: "paused", labelKey: "flows.pausedStatus", color: "info" },
  { value: "queued", labelKey: "flows.queuedStatus", color: "default" },
  { value: "timeout", labelKey: "flows.timeoutStatus", color: "error" },
  { value: "stopped", labelKey: "flows.stoppedStatus", color: "default" },
  { value: "canceled", labelKey: "flows.canceledStatus", color: "default" },
  {
    value: "internal_error",
    labelKey: "flows.internalErrorStatus",
    color: "error",
  },
  {
    value: "memory_limit_exceeded",
    labelKey: "flows.memoryLimitExceededStatus",
    color: "error",
  },
];
const RUN_STATUS_COLOR_BY_VALUE = Object.fromEntries(
  RUN_STATUS_CONFIG.map(({ value, color }) => [value, color]),
);

const FlowCard = ({ flow, onOpenFlow }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = () => {
    if (onOpenFlow) {
      onOpenFlow(flow);
    } else {
      // Ensure both projectId and id are present before navigation
      if (flow.projectId && flow.id) {
        navigate(`/flows/${flow.projectId}/${flow.id}`);
      } else {
        console.error("Missing projectId or flowId for navigation:", flow);
      }
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: (theme) =>
            `0 8px 16px ${
              theme.palette.mode === "light"
                ? "rgba(0, 0, 0, 0.2)"
                : "rgba(0, 0, 0, 0.4)"
            }`,
        },
        cursor: "pointer",
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            noWrap
            sx={{ mb: 1, maxWidth: "80%" }}
          >
            {flow.version?.displayName || "Unnamed Flow"}
          </Typography>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            aria-label="flow options"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Chip
          label={flow.status || "DISABLED"}
          color={getStatusColor(flow.status)}
          size="small"
          sx={{ mb: 2 }}
        />

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2">{formatDate(flow.created)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Updated
            </Typography>
            <Typography variant="body2">{formatDate(flow.updated)}</Typography>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Project
            </Typography>
            <Typography variant="body2" noWrap>
              {t("common.project")}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/flows/${flow.projectId}/${flow.id}`);
          }}
        >
          View Flow
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(`/flows/${flow.projectId}/${flow.id}`);
          }}
        >
          View Flow
        </MenuItem>
      </Menu>
    </Card>
  );
};

const CreateFlow = ({ text, onFlowCreated, open, setOpen }) => {
  const { t } = useTranslation();
  const { availableProjects } = useProjects();
  const { currentOrganization } = useOrganization();
  const isViewer = !availableProjects.some(
    (project) => project.accessLevel !== "viewer",
  );
  const [flowData, setFlowData] = useState({
    display_name: "",
    project_id: "",
  });

  const handleAttribute = (attribute) => {
    return (e) => {
      setFlowData((old) => ({
        ...old,
        [attribute]: e.target.value,
      }));
    };
  };

  const createFlow = async () => {
    try {
      const response = await flowsApi.createFlow({ ...flowData, metadata: {} });
      setOpen(false);
      setFlowData({ display_name: "", project_id: "" });
      if (onFlowCreated) {
        onFlowCreated();
      }
    } catch (error) {
      console.error("Error creating flow:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to create flow. Please try again.";
      toast.error(errorMessage);
    }
  };
  console.log("available", availableProjects);
  return (
    <>
      <Tooltip title={isViewer ? t("flows.viewerCannotCreate") : ""}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            width: { xs: "100%", sm: "100%", md: "auto", lg: "auto" },
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              setOpen(true);
            }}
            disabled={isViewer}
            sx={{
              minWidth: { xs: "100%", sm: "100%", md: "auto", lg: "auto" },
            }}
          >
            {t("flows.newFlow")}
          </Button>
        </Box>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {text || t("flows.createFlow")}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", insetInlineEnd: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  name="display_name"
                  label={t("flows.flowName")}
                  fullWidth
                  value={flowData.display_name}
                  onChange={handleAttribute("display_name")}
                />
                <FormHelperText id="flow-name-helper-text">
                  {t("flows.flowNameHelper")}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="project-select-label">
                  {t("flows.project")}
                </InputLabel>
                <Select
                  labelId="project-select-label"
                  name="project_id"
                  label="Project"
                  fullWidth
                  value={flowData.project_id}
                  onChange={handleAttribute("project_id")}
                >
                  {availableProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText id="project-select-helper-text">
                  {t("flows.projectHelper")}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setOpen(false);
              setFlowData({ display_name: "", project_id: "" });
            }}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Tooltip title={isViewer ? t("flows.viewerCannotCreate") : ""}>
            <span>
              <Button
                onClick={createFlow}
                variant="contained"
                color="primary"
                disabled={isViewer}
              >
                {t("common.create")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FlowsList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const theme = useTheme();
  const { mode } = useThemeMode();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewFilter, setViewFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // TODO: Uncomment when connection filtering is implemented
  // const [connectionsFilter, setConnectionsFilter] = useState('all');
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [open, setOpen] = useState(false);
  const { selectedProjects, availableProjects } = useProjects();
  const { currentOrganization } = useOrganization();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () => (Array.isArray(availableProjects) ? availableProjects : []),
    [availableProjects],
  );
  const isViewer = !safeAvailableProjects.some(
    (project) => project.accessLevel !== "viewer",
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Tab state - defaults to Flows, but a caller (e.g. TemplateDetail's back
  // button) can request a specific tab via navigate("/flows", { state: { activeTab } })
  // so returning from a template's detail page lands back on Explore, not Flows.
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Runs state
  const [runsRowsPerPage, setRunsRowsPerPage] = useState(10);
  // Cursor-based pagination, not a page index: the backend pages runs by an
  // opaque cursor with no notion of a total count, so "page N" cannot be
  // computed. `cursors` holds the cursor used to reach each page BEFORE the
  // current one, so its length is the current page's depth and popping it
  // walks back. `queryKey` records which query those cursors belong to, so a
  // stack from a previous filter/selection is recognisably stale rather than
  // silently reused (see activeCursorStack).
  const [runsCursorStack, setRunsCursorStack] = useState({
    queryKey: null,
    cursors: [],
  });

  // Runs filter state
  const [runsSearchTerm, setRunsSearchTerm] = useState("");
  const [runsStatusFilter, setRunsStatusFilter] = useState("all");
  const [runsFlowFilter, setRunsFlowFilter] = useState("all");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewFilterChange = (event) => {
    setViewFilter(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // TODO: Uncomment when connection filtering is implemented
  // const handleConnectionsFilterChange = (event) => {
  //     setConnectionsFilter(event.target.value);
  // };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchFlows = async () => {
    try {
      setLoading(true);

      // Determine which projects to fetch flows for
      const projectsToFetch =
        safeSelectedProjects.includes("all") ||
        safeSelectedProjects.length === 0
          ? safeAvailableProjects
          : safeAvailableProjects.filter((project) =>
              safeSelectedProjects.includes(project.id),
            );
      // Single API call with array of project IDs
      const projectIds = projectsToFetch.map((project) => project.id);

      const response = await flowsApi.getFlows({
        project_ids: projectIds, // Pass array of project IDs for single call
        include_shared: viewFilter === "shared",
      });

      const allFlows = response.data || [];
      setFlows(allFlows);
    } catch (error) {
      console.error("Error fetching flows:", error);
      setFlows([]);
    } finally {
      setLoading(false);
    }
  };

  // The projects the runs list covers: every project the user can see when the
  // selection is "all" or empty, else exactly what they picked.
  const runsProjectIds =
    safeSelectedProjects.includes("all") || safeSelectedProjects.length === 0
      ? safeAvailableProjects.map((project) => project.id)
      : safeSelectedProjects;

  // Identifies the exact runs QUERY, independent of which page of it is being
  // viewed. A cursor is only meaningful against the query that produced it, so
  // when this key changes every collected cursor is void and paging restarts
  // at page one.
  const runsQueryKey = JSON.stringify([
    runsProjectIds,
    runsRowsPerPage,
    runsStatusFilter,
  ]);

  // The cursor stack is stored WITH the query key it belongs to and read back
  // through this guard, so a stack left over from a previous query can never
  // be used against the new one. That is what makes the reset SYNCHRONOUS:
  // resetting from a separate effect would leave the fetch effect running once
  // in the same commit with the OLD stack and the NEW filters - firing a
  // request for a combination that never existed, ahead of a second corrected
  // one. Deriving instead of resetting means exactly one request per change.
  const activeCursorStack =
    runsCursorStack.queryKey === runsQueryKey ? runsCursorStack.cursors : [];
  const runsCursor = activeCursorStack[activeCursorStack.length - 1] || "";

  const {
    data: runsResponse,
    isLoading: runsLoading,
    isFetching: runsFetching,
    isError: isRunsError,
    error: runsErrorObject,
  } = useFlowRuns({
    projectIds: runsProjectIds,
    status: runsStatusFilter,
    cursor: runsCursor,
    limit: runsRowsPerPage,
    enabled: activeTab === 1,
  });
  // isError alone isn't enough to hide the table: placeholderData keeps the
  // previous page's rows around across the failed refetch (see
  // useFlowRuns.ts), so a background poll/page-turn failure must be checked
  // BEFORE falling back to "show whatever rows we have" - otherwise a run
  // that started failing 7s ago keeps rendering stale data with no signal
  // anything is wrong.
  const flowRuns = isRunsError ? [] : runsResponse?.data || [];
  const runsNextCursor = isRunsError ? null : runsResponse?.next || null;
  const runsError = isRunsError
    ? runsErrorObject?.message || t("flows.failedToFetchRuns")
    : null;

  useEffect(() => {
    // Always fetch flows on mount and when Flows tab is selected
    if (activeTab === 0) {
      fetchFlows();
    }
  }, [activeTab, viewFilter]);

  // Keyed on the dropped-ids CONTENT (joined to a stable string), not the
  // runsResponse object - refetchInterval hands back a new object every poll
  // even when droppedProjectIds is unchanged, which would otherwise re-fire
  // this warning every 7s for as long as an unauthorized project stays
  // selected.
  const droppedProjectIdsKey = (runsResponse?.droppedProjectIds || []).join(
    ",",
  );
  useEffect(() => {
    if (droppedProjectIdsKey) {
      console.warn(
        "Some selected projects were excluded from the runs list because you do not have read access to them:",
        droppedProjectIdsKey.split(","),
      );
    }
  }, [droppedProjectIdsKey]);

  const filteredFlows = flows.filter((flow) => {
    const flowName = flow.version?.displayName || "";
    const matchesSearch = flowName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || flow.status === statusFilter;

    // Add additional filters here as needed
    return matchesSearch && matchesStatus;
  });

  // Get paginated data
  const paginatedFlows = filteredFlows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Runs are already paged and status-filtered by the backend; only the
  // flow-name text search and the flow picker stay client-side, because the
  // API has no flow-name search and its flowId param selects a different,
  // single-project code path. Both therefore filter WITHIN the current page
  // rather than across the whole result set.
  const visibleFlowRuns = flowRuns.filter((run) => {
    const matchesSearch =
      !runsSearchTerm ||
      (run.flowDisplayName || "")
        .toLowerCase()
        .includes(runsSearchTerm.toLowerCase());
    const matchesFlow =
      runsFlowFilter === "all" || run.flowId === runsFlowFilter;
    return matchesSearch && matchesFlow;
  });

  // Render skeleton rows while loading
  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell align={isRTL ? "right" : "left"}>
          <Skeleton variant="text" width="100%" />
        </TableCell>
        <TableCell align={isRTL ? "right" : "left"}>
          <Skeleton variant="text" width="100%" />
        </TableCell>
        <TableCell align={isRTL ? "right" : "left"}>
          <Skeleton variant="text" width="100%" />
        </TableCell>
        <TableCell align={isRTL ? "right" : "left"}>
          <Skeleton variant="text" width="100%" />
        </TableCell>
        <TableCell align={isRTL ? "right" : "left"}>
          <Skeleton variant="text" width="60px" />
        </TableCell>
      </TableRow>
    ));
  };

  const handleToggleStatus = async (flow, event) => {
    event.stopPropagation();
    event.preventDefault();
    const newStatus = flow.status === "ENABLED" ? "DISABLED" : "ENABLED";
    try {
      const updatedFlows = flows.map((f) =>
        f.id === flow.id ? { ...f, status: newStatus } : f,
      );
      setFlows(updatedFlows);
      try {
        await flowsApi.toggleFlowStatus(flow.id, {
          type: "CHANGE_STATUS",
          request: {
            status: newStatus === "ENABLED" ? "ENABLED" : "DISABLED",
          },
        });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await updateKeycloakToken();
          await flowsApi.toggleFlowStatus(flow.id, {
            type: "CHANGE_STATUS",
            request: {
              status: newStatus === "ENABLED" ? "ENABLED" : "DISABLED",
            },
          });
        } else {
          throw error;
        }
      }
      toast.success(
        newStatus === "ENABLED"
          ? t("flows.flowEnabled")
          : t("flows.flowDisabled"),
      );
    } catch (error) {
      console.error(`Error toggling flow status:`, error);
      const revertedFlows = flows.map((f) =>
        f.id === flow.id ? { ...f, status: flow.status } : f,
      );
      setFlows(revertedFlows);
      // Surface the real reason (e.g. no budget / read-only) instead of silently
      // reverting - AP's status + message is preserved by the backend proxy
      // (operate_on_flow) and returned as error.response.data.detail.
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        t("flows.statusChangeFailed");
      toast.error(errorMessage);
    }
  };

  const handleFlowAction = (action, flow) => {
    if (action === "delete") {
      setFlowToDelete(flow);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteFlow = async () => {
    if (!flowToDelete) return;
    setDeleteInProgress(true);
    setDeleteError(null);
    try {
      try {
        await flowsApi.deleteFlow(flowToDelete.id);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await updateKeycloakToken();
          await flowsApi.deleteFlow(flowToDelete.id);
        } else {
          throw error;
        }
      }
      toast.success(t("flows.flowDeletedSuccess"));
      setDeleteDialogOpen(false);
      setFlowToDelete(null);
      fetchFlows();
    } catch (error) {
      console.error("Error deleting flow:", error);
      setDeleteError(
        error.response?.data?.message ||
          error.message ||
          "An unknown error occurred while deleting the flow",
      );
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleMenuOpen = (event, flow) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFlow(null);
  };

  // Add reset function
  const handleResetAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    // TODO: Uncomment when connection filtering is implemented
    // setConnectionsFilter('all');
    setViewFilter("all");
    // Fetch flows after resetting filters
    setTimeout(() => fetchFlows(), 0);
  };

  // Add reset function for runs
  const handleResetRunsFilters = () => {
    setRunsSearchTerm("");
    setRunsStatusFilter("all");
    setRunsFlowFilter("all");
    // No explicit fetch: clearing the filters changes useFlowRuns' own query
    // key, which refetches (and resets the cursor stack) on its own.
  };

  // Both handlers only move the cursor stack; useFlowRuns owns the request
  // so a page turn can never fire two. Each write stamps the stack with the
  // query it belongs to, so it is recognisably stale if the query then changes.
  const handleRunsNextPage = () => {
    if (!runsNextCursor) {
      return;
    }
    setRunsCursorStack({
      queryKey: runsQueryKey,
      cursors: [...activeCursorStack, runsNextCursor],
    });
  };

  const handleRunsPreviousPage = () => {
    setRunsCursorStack({
      queryKey: runsQueryKey,
      cursors: activeCursorStack.slice(0, -1),
    });
  };

  const handleRunsRowsPerPageChange = (event) => {
    setRunsRowsPerPage(parseInt(event.target.value, 10));
  };

  // Get status color for run status
  const getRunStatusColor = (status) =>
    RUN_STATUS_COLOR_BY_VALUE[status?.toLowerCase()] ?? "default";

  // Format duration
  const formatDuration = (durationMs) => {
    if (durationMs < 1000) {
      return `${durationMs}ms`;
    } else if (durationMs < 60000) {
      return `${Math.floor(durationMs / 1000)}s`;
    } else {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  };

  const getRunDurationLabel = (run) => {
    if (run?.durationDisplay) {
      return run.durationDisplay;
    }

    if (typeof run?.duration === "number") {
      return formatDuration(run.duration);
    }

    return t("common.unknown");
  };

  // SVG components for arrows (color is now 'currentColor' for inheritance)
  const LeftArrow = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        verticalAlign: "middle",
        [isRTL ? "marginInlineStart" : "marginInlineEnd"]: 8,
      }}
    >
      <path
        d="M13.0117 6.26172V7.73828H3.87109L8.05469 11.957L7 13.0117L0.988281 7L7 0.988281L8.05469 2.04297L3.87109 6.26172H13.0117Z"
        fill="currentColor"
      />
    </svg>
  );
  const RightArrow = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        verticalAlign: "middle",
        [!isRTL ? "marginInlineStart" : "marginInlineEnd"]: 8,
      }}
    >
      <path
        d="M7 0.988281L13.0117 7L7 13.0117L5.94531 11.957L10.1289 7.73828H0.988281V6.26172H10.1289L5.94531 2.04297L7 0.988281Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <DashboardLayout title={t("flows.title")}>
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {activeTab === 2 ? t("flows.exploreHeading") : t("flows.heading")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: "16px", fontWeight: 400 }}
            >
              {activeTab === 2 ? t("flows.exploreSubtitle") : t("flows.subtitle")}
            </Typography>
          </Grid>
          {activeTab !== 2 && (
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <CreateFlow
                onFlowCreated={fetchFlows}
                open={open}
                setOpen={setOpen}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="flow tabs"
          sx={{
            "& .MuiTab-root": {
              color: "text.secondary",
              fontSize: "16px",
              fontWeight: 400,
            },
            "& .Mui-selected": {
              color: `${theme.palette.primary.main} !important`,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab
            icon={<FlowsIcon sx={{ transform: "rotate(360deg)" }} />}
            iconPosition="start"
            label={t("flows.flowsTab")}
            id="tab-0"
          />
          <Tab
            icon={<HistoryIcon className="rtl:scale-x-[-1]" />}
            iconPosition="start"
            label={t("flows.runsTab")}
            id="tab-1"
          />
          <Tab
            icon={<ExploreIcon />}
            iconPosition="start"
            label={t("flows.exploreTab")}
            id="tab-2"
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={10} md={3.5} sx={{ position: "relative" }}>
                <TextField
                  placeholder={t("flows.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "center",
                }}
              >
                <Tooltip title={t("flows.resetFilters")}>
                  <span>
                    <IconButton
                      onClick={handleResetAllFilters}
                      size="small"
                      disabled={
                        !searchTerm &&
                        statusFilter === "all" &&
                        viewFilter === "all"
                      }
                      sx={{
                        color:
                          !searchTerm &&
                          statusFilter === "all" &&
                          viewFilter === "all"
                            ? "text.disabled"
                            : "text.primary",
                      }}
                    >
                      <ResetIcon className="rtl:scale-x-[-1]" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="status-filter-label">
                    {t("common.status")}
                  </InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label="Status"
                  >
                    <MenuItem value="all">{t("flows.allStatuses")}</MenuItem>
                    <MenuItem value="ENABLED">{t("flows.enabled")}</MenuItem>
                    <MenuItem value="DISABLED">{t("flows.disabled")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* TODO: Uncomment when connection filtering is implemented
                            <Grid item xs={12} md={2}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel id="connections-filter-label">Connections</InputLabel>
                                    <Select
                                        labelId="connections-filter-label"
                                        id="connections-filter"
                                        value={connectionsFilter}
                                        onChange={handleConnectionsFilterChange}
                                        label="Connections"
                                    >
                                        <MenuItem value="all">All Connections</MenuItem>
                                        <MenuItem value="api">API</MenuItem>
                                        <MenuItem value="database">Database</MenuItem>
                                        <MenuItem value="third-party">Third Party</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            */}
              <Grid item xs={12} md={5}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="view-filter-label">
                    {t("flows.viewFilter")}
                  </InputLabel>
                  <Select
                    labelId="view-filter-label"
                    id="view-filter"
                    value={viewFilter}
                    onChange={handleViewFilterChange}
                    label={t("flows.viewFilter")}
                  >
                    <MenuItem value="all">{t("flows.allFlows")}</MenuItem>
                    <MenuItem value="owned">{t("flows.myFlows")}</MenuItem>
                    <MenuItem value="shared">
                      {t("flows.sharedWithMe")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={1}
                sx={{
                  display: { xs: "none", md: "flex" },
                  justifyContent: "flex-end",
                }}
              >
                <Tooltip title={t("flows.resetFilters")}>
                  <span>
                    <IconButton
                      onClick={handleResetAllFilters}
                      size="small"
                      disabled={
                        !searchTerm &&
                        statusFilter === "all" &&
                        viewFilter === "all"
                      }
                      sx={{
                        color:
                          !searchTerm &&
                          statusFilter === "all" &&
                          viewFilter === "all"
                            ? "text.disabled"
                            : "text.primary",
                      }}
                    >
                      <ResetIcon className="rtl:scale-x-[-1]" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="flows table">
              <TableHead>
                <TableRow>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.name")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("flows.steps")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.created")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.status")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  renderSkeletonRows()
                ) : paginatedFlows.length > 0 ? (
                  paginatedFlows.map((flow) => (
                    <TableRow
                      key={flow.id}
                      hover
                      onClick={() =>
                        navigate(`/flows/${flow.projectId}/${flow.id}`)
                      }
                      sx={{
                        cursor: "pointer",
                        "& > *": {
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "text.primary",
                        },
                      }}
                    >
                      <TableCell align={isRTL ? "right" : "left"}>
                        {flow.version?.displayName || t("flows.unnamedFlow")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <FlowStepIcons trigger={flow.version?.trigger} />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {formatDate(flow.created)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {flow.publishedVersionId !== null ? (
                          <div
                            style={{ height: "20px" }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Switch
                              className="ms-[-12px]"
                              checked={flow.status === "ENABLED"}
                              onChange={(e) => handleToggleStatus(flow, e)}
                              onClick={(e) => e.stopPropagation()}
                              color="primary"
                              // sx={{
                              //   width: 44,
                              //   height: 27,
                              //   padding: 0,
                              //   "& .MuiSwitch-switchBase": {
                              //     padding: 0.45,
                              //     "&.Mui-checked": {
                              //       transform: "translateX(20px)",
                              //       color: theme.palette.common.white,
                              //       "&:hover": {
                              //         backgroundColor: alpha(
                              //           theme.palette.common.white,
                              //           0.1,
                              //         ),
                              //       },
                              //       "& + .MuiSwitch-track": {
                              //         backgroundColor:
                              //           theme.palette.success.main,
                              //         opacity: 1,
                              //       },
                              //     },
                              //   },
                              //   "& .MuiSwitch-track": {
                              //     width: 44,
                              //     height: 25,
                              //     borderRadius: 2,
                              //     opacity: 0.3,
                              //     backgroundColor: theme.palette.text.disabled,
                              //   },
                              //   "& .MuiSwitch-thumb": {
                              //     width: 18,
                              //     height: 18,
                              //     boxShadow:
                              //       mode === "light"
                              //         ? "0 1px 3px rgba(0,0,0,0.3)"
                              //         : "0 1px 3px rgba(255,255,255,0.1)",
                              //     transition:
                              //       "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                              //   },
                              // }}
                            />
                          </div>
                        ) : (
                          <div style={{ height: "20px" }}>
                            <Chip
                              label={t("flows.draft")}
                              color="default"
                              size="small"
                            />
                          </div>
                        )}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, flow)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {searchTerm || viewFilter !== "all"
                          ? t("flows.noMatchingFlows")
                          : t("flows.noFlowsYet")}
                      </Typography>
                      {!searchTerm && viewFilter === "all" && (
                        <Tooltip
                          title={isViewer ? t("flows.viewerCannotCreate") : ""}
                        >
                          <span>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => setOpen(true)}
                              disabled={
                                !safeAvailableProjects.some(
                                  (project) => project.accessLevel !== "viewer",
                                )
                              }
                            >
                              {t("flows.createFirstFlow")}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              mt: 2,
              gap: 1,
            }}
          >
            <Button
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              sx={{
                color: "primary.main",
                background: "none",
                boxShadow: "none",
                border: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-disabled": { color: "text.disabled" },
              }}
            >
              {isRTL ? <RightArrow /> : <LeftArrow />}
              {t("common.previous")}
            </Button>
            <FormControl sx={{ width: 120 }}>
              <InputLabel
                id="flows-rows-per-page-label"
                sx={{ color: "text.secondary" }}
              >
                {t("common.rowsPerPage")}
              </InputLabel>
              <Select
                labelId="flows-rows-per-page-label"
                id="flows-rows-per-page"
                value={rowsPerPage}
                label={t("common.rowsPerPage")}
                onChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiSvgIcon-root": { color: "primary.main" },
                }}
              >
                {[5, 10, 25, 50].map((opt) => (
                  <MenuItem
                    key={opt}
                    value={opt}
                    sx={{ color: "text.primary", fontSize: 18 }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={() => handleChangePage(null, page + 1)}
              disabled={
                page >= Math.ceil(filteredFlows.length / rowsPerPage) - 1
              }
              sx={{
                color: "primary.main",
                background: "none",
                boxShadow: "none",
                border: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-disabled": { color: "text.disabled" },
              }}
            >
              {t("common.next")}
              {isRTL ? <LeftArrow /> : <RightArrow />}
            </Button>
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 4 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                item
                xs={10}
                md={4}
                sx={{ position: "relative", flexGrow: 1 }}
              >
                <TextField
                  placeholder={t("flows.searchRunsPlaceholder")}
                  value={runsSearchTerm}
                  onChange={(e) => setRunsSearchTerm(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ flexGrow: 1 }}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="run-status-filter-label">
                    {t("common.status")}
                  </InputLabel>
                  <Select
                    labelId="run-status-filter-label"
                    id="run-status-filter"
                    value={runsStatusFilter}
                    onChange={(e) => setRunsStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">{t("flows.allStatuses")}</MenuItem>
                    {RUN_STATUS_CONFIG.map(({ value, labelKey }) => (
                      <MenuItem key={value} value={value}>
                        {t(labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ flexGrow: 1 }}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="run-flow-filter-label">
                    {t("flows.flowFilter")}
                  </InputLabel>
                  <Select
                    labelId="run-flow-filter-label"
                    id="run-flow-filter"
                    value={runsFlowFilter}
                    onChange={(e) => setRunsFlowFilter(e.target.value)}
                    label={t("flows.flowFilter")}
                  >
                    <MenuItem value="all">{t("flows.allFlows")}</MenuItem>
                    {flows.map((flow) => (
                      <MenuItem key={flow.id} value={flow.id}>
                        {flow.version?.displayName || t("flows.unnamedFlow")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={2}
                md={1}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Tooltip title={t("flows.resetFilters")}>
                  <span>
                    <IconButton
                      onClick={handleResetRunsFilters}
                      disabled={
                        !runsSearchTerm &&
                        runsStatusFilter === "all" &&
                        runsFlowFilter === "all"
                      }
                      size="small"
                      sx={{
                        color:
                          !runsSearchTerm &&
                          runsStatusFilter === "all" &&
                          runsFlowFilter === "all"
                            ? "text.disabled"
                            : "text.primary",
                      }}
                    >
                      <ResetIcon className="rtl:scale-x-[-1]" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="flow runs table">
              <TableHead>
                <TableRow>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("flows.flow")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("flows.startedAt")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("flows.duration")}
                  </TableCell>
                  <TableCell align={isRTL ? "right" : "left"}>
                    {t("common.status")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runsLoading ? (
                  Array.from({ length: runsRowsPerPage }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : visibleFlowRuns.length > 0 ? (
                  visibleFlowRuns.map((run) => (
                    <TableRow
                      key={run.id}
                      hover
                      onClick={() => {
                        if (run.projectId && run.flowId) {
                          navigate(`/flows/${run.projectId}/${run.flowId}`);
                        } else {
                          console.error(
                            "Missing projectId or flowId for navigation:",
                            run,
                          );
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                        "& > *": {
                          fontSize: "16px",
                          fontWeight: 400,
                          color: "text.primary",
                          borderBottom: "none",
                        },
                      }}
                    >
                      <TableCell align={isRTL ? "right" : "left"}>
                        {run.flowDisplayName || t("common.unknown")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {formatDate(run.startTime)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {getRunDurationLabel(run)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Chip
                          label={run.statusLabel || run.status || "Unknown"}
                          color={getRunStatusColor(run.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {runsError
                          ? t("flows.errorLoadingRuns", { error: runsError })
                          : t("flows.noFlowRunsFound")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              mt: 2,
              gap: 2,
            }}
          >
            <Button
              onClick={handleRunsPreviousPage}
              disabled={activeCursorStack.length === 0}
              sx={{
                color: "primary.main",
                background: "none",
                boxShadow: "none",
                border: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-disabled": { color: "text.disabled" },
              }}
            >
              {isRTL ? <RightArrow /> : <LeftArrow />}
              {t("common.previous")}
            </Button>
            <FormControl sx={{ width: 120 }}>
              <InputLabel
                shrink
                id="runs-rows-per-page-label"
                sx={{ color: "text.secondary" }}
              >
                {t("common.rowsPerPage")}
              </InputLabel>
              <Select
                labelId="runs-rows-per-page-label"
                id="runs-rows-per-page"
                value={runsRowsPerPage}
                onChange={handleRunsRowsPerPageChange}
                label={t("common.rowsPerPage")}
                sx={{
                  "& .MuiSvgIcon-root": { color: "primary.main" },
                }}
              >
                {[5, 10, 25, 50].map((opt) => (
                  <MenuItem
                    key={opt}
                    value={opt}
                    sx={{ color: "text.primary", fontSize: 18 }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              onClick={handleRunsNextPage}
              disabled={!runsNextCursor}
              sx={{
                color: "primary.main",
                background: "none",
                boxShadow: "none",
                border: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                display: "flex",
                alignItems: "center",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-disabled": { color: "text.disabled" },
              }}
            >
              {t("common.next")}
              {isRTL ? <LeftArrow /> : <RightArrow />}
            </Button>
          </Box>
        </>
      )}

      {activeTab === 2 && (
        <ExploreTemplates
          onTemplateClick={(templateId) =>
            navigate(`/flows/explore/${templateId}`)
          }
        />
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <Tooltip title={isViewer ? t("flows.viewerCannotDelete") : ""}>
          <span>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                if (selectedFlow) {
                  handleFlowAction("delete", selectedFlow);
                }
              }}
              disabled={isViewer}
              sx={{
                color: "error.main",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              {t("flows.deleteFlow")}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteInProgress && setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: theme.shadows[8],
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {t("flows.deleteFlow")}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            {t("common.cannotBeUndone")}
          </Alert>
          <Typography id="alert-dialog-description">
            {t("flows.deleteFlowConfirm", {
              name:
                flowToDelete?.version?.displayName || t("flows.unnamedFlow"),
            })}
          </Typography>

          {deleteError && (
            <Typography
              color="error"
              sx={{
                mt: 2,
                p: 1,
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderRadius: "4px",
              }}
            >
              Error: {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteInProgress}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Tooltip title={isViewer ? t("flows.viewerCannotDelete") : ""}>
            <span>
              <Button
                onClick={handleDeleteFlow}
                variant="contained"
                color="error"
                disabled={deleteInProgress || isViewer}
              >
                {deleteInProgress ? t("common.deleting") : t("common.delete")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

const USE_IFRAME_BUILDER = window.env?.REACT_APP_USE_IFRAME_BUILDER === "true";

const Flows = () => {
  const theme = useTheme();
  const { ProjectId, flowId } = useParams();
  const navigate = useNavigate();
  const { selectedProjects, availableProjects } = useProjects();
  const { currentOrganization, organizations, setOrganization } =
    useOrganization();

  // availableProjects is scoped to whichever org is "current" (restored from
  // localStorage, unrelated to the flow being opened). So a direct
  // /flows/:ProjectId/:flowId link for a project under a DIFFERENT org than the
  // current one never resolves on its own - currentProject/apProjectId stay
  // undefined and isApProjectIdReady never flips true, hanging the page. (This is
  // a client resolution gap, not an AP authorization one - the token check for
  // that user/flow succeeds.)
  //
  // So: if the URL's project id is NOT in availableProjects once that list has
  // loaded, fetch the project directly (org-independent) and switch to ITS real
  // organization - the org a flow belongs to must win over whatever org was
  // selected before this navigation.
  useEffect(() => {
    if (!ProjectId || !Array.isArray(availableProjects)) return;
    const alreadyVisible = availableProjects.some((p) => p.id === ProjectId);
    if (alreadyVisible) return;

    let cancelled = false;
    projectsApi
      .getProject(ProjectId)
      .then((project) => {
        if (cancelled || !project) return;
        if (!project.organization_id) return; // personal project - nothing to switch
        if (project.organization_id === currentOrganization?.id) return;

        const targetOrg = organizations.find(
          (org) => org.id === project.organization_id,
        );
        if (targetOrg) {
          setOrganization(targetOrg);
          return;
        }
        // Org not in the already-loaded list (e.g. stale organizations
        // cache) - resolve it directly rather than silently giving up.
        // organizationApi (unlike rag-service's own api objects) returns the
        // raw axios response, not an already-unwrapped body.
        return organizationApi
          .getOrganization(project.organization_id)
          .then((res) => {
            if (!cancelled && res?.data) setOrganization(res.data);
          });
      })
      .catch(() => {
        // A failed lookup here must never crash the page - the existing
        // "flow stays stuck loading" behavior for a genuinely broken
        // project id is unchanged; this effect only helps the common case
        // (project exists, just under a different org) resolve correctly.
      });
    return () => {
      cancelled = true;
    };
  }, [ProjectId, availableProjects, currentOrganization, organizations, setOrganization]);

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );

  // The flow's real AP-side home (shared-project aware) - NOT necessarily the
  // caller's own personal AP project. Resolved from the already-fetched project
  // list (ProjectContext) rather than a new API call.
  const currentProject = useMemo(
    () =>
      Array.isArray(availableProjects)
        ? availableProjects.find((project) => project.id === ProjectId)
        : undefined,
    [availableProjects, ProjectId],
  );
  // Fallback project details resolved directly (see the effect below): the
  // primary source is ProjectContext's cached `availableProjects`, but that
  // list can be stale for a JUST-CREATED project - ProjectContext.createProject
  // optimistically appends the new project WITHOUT apProjectId/organizationId/
  // userId rather than re-fetching. For a flow created in such a project,
  // `apProjectId` is undefined, so `isApProjectIdReady` stays false forever and the
  // builder hangs on a grey skeleton (the flow itself loads fine - only these
  // derived fields are missing; a reload fixes it because fetchProjects re-runs).
  // The effect below fills these in from a direct getProject call whenever the
  // cached context copy is missing them.
  const [resolvedProject, setResolvedProject] = useState(null);
  const apProjectId = currentProject?.apProjectId ?? resolvedProject?.ap_project_id;
  // Kept in a ref so the flowId-scoped resolution effect below can read the
  // CURRENT context value without adding currentProject to its deps (which
  // would needlessly re-fetch the flow on every context change).
  const apProjectIdFromContextRef = useRef(currentProject?.apProjectId);
  apProjectIdFromContextRef.current = currentProject?.apProjectId;
  // Real Bunyan org id (org-scoped project) or user id (personal project) - the
  // same externalId convention app_connections.py's create_app_connection route
  // already uses server-side. Delivered to the iframe over the existing auth
  // handshake so a connection created directly inside the AP builder (which
  // otherwise falls back to a random, ungrantable id) can be tagged with a real,
  // grantable tenant instead of failing the Bunyan authorization hook's Job C.
  // Same stale-context fallback as apProjectId above.
  const apTenantId =
    currentProject?.organizationId ??
    currentProject?.userId ??
    resolvedProject?.organization_id ??
    resolvedProject?.user_id;

  // The iframe builder addresses flows by their real AP id, not rag-agents'
  // own local flowId (route param) - fetch the flow once to resolve it. The
  // response's top-level `id` is the LOCAL rag-agents id and `externalId` is AP's
  // own tenant tag (an unrelated, same-named concept); only `apFlowId` carries the
  // real AP flow id. Passing the local id through would 400 in the builder
  // ("must match pattern /^[0-9a-zA-Z]{21}$/").
  //
  // apFlowId MUST be reset to null immediately on flowId change: navigating
  // client-side from one flow to another (no reload, same mounted component) would
  // otherwise keep the previous flow's apFlowId/apProjectId truthy while the new
  // ones load. isApProjectIdReady only checks truthiness, not "does this value
  // belong to the current flowId", so the iframe could briefly render against the
  // wrong flow. Guarded by resetting on change AND tracking which flowId the
  // resolved apFlowId belongs to (resolvedForFlowId).
  const [apFlowId, setApFlowId] = useState(null);
  const [resolvedForFlowId, setResolvedForFlowId] = useState(null);
  useEffect(() => {
    if (!USE_IFRAME_BUILDER || !flowId) {
      setApFlowId(null);
      setResolvedForFlowId(null);
      setResolvedProject(null);
      return;
    }
    setApFlowId(null);
    setResolvedForFlowId(null);
    setResolvedProject(null);
    let cancelled = false;
    flowsApi.getFlow(flowId).then((flow) => {
      if (cancelled) {
        return;
      }
      setApFlowId(flow?.apFlowId ?? null);
      setResolvedForFlowId(flowId);
      // Safety net for a stale ProjectContext (see resolvedProject above):
      // if the flow's project isn't in the cached list yet, or is there
      // without its apProjectId, resolve it directly from the flow's own
      // project rather than hanging on the grey skeleton. getFlow returns the
      // LOCAL project id as `projectId`; getProject returns the raw project,
      // whose ap_project_id / organization_id / user_id are the same fields
      // fetchProjects maps into context.
      const flowProjectId = flow?.projectId;
      if (!apProjectIdFromContextRef.current && flowProjectId) {
        projectsApi.getProject(flowProjectId).then((project) => {
          if (!cancelled) {
            setResolvedProject(project ?? null);
          }
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const isApProjectIdReady = USE_IFRAME_BUILDER
    ? Boolean(apProjectId) && Boolean(apFlowId) && resolvedForFlowId === flowId
    : true;

  // Check if the current flow belongs to the selected project
  useEffect(() => {
    // Only check if we're on a flow detail page
    if (flowId && ProjectId) {
      const isAllProjectsSelected =
        safeSelectedProjects.length === 0 ||
        safeSelectedProjects.includes("all");

      // If specific projects are selected and current flow's project is not among them,
      // redirect to the flows listing page
      if (!isAllProjectsSelected && !safeSelectedProjects.includes(ProjectId)) {
        navigate("/flows");
      }
    }
  }, [flowId, ProjectId, safeSelectedProjects, navigate]);

  // The <Omniflow> backup path resolves its own AP project internally via
  // apTokenManager, but keep it pointed at the flow's real (shared-project-aware)
  // AP home too, so both paths agree on which AP project is being edited.
  useEffect(() => {
    if (!USE_IFRAME_BUILDER && apProjectId && window.apTokenManager) {
      window.apTokenManager.setProjectId(apProjectId);
    }
  }, [apProjectId]);

  return flowId && ProjectId ? (
    <DashboardLayout
      title={flowId ? "Flow Details" : "Create Flow"}
      noPadding={true}
      fullHeight={true}
    >
      <div
        style={{
          width: "calc(100vw - 71px)",
          height: "100%",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {!isApProjectIdReady ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Skeleton variant="rectangular" width="80%" height="60%" />
          </Box>
        ) : USE_IFRAME_BUILDER ? (
          <ApBuilderIframe
            projectId={apProjectId}
            flowId={apFlowId}
            tenantId={apTenantId}
            realProjectId={currentProject?.id}
            onBack={() => navigate("/flows")}
          />
        ) : (
          <Omniflow
            projectId={ProjectId}
            flowId={flowId}
            apiService={apiService}
            onBack={() => navigate("/flows")}
          />
        )}
      </div>
    </DashboardLayout>
  ) : (
    <FlowsList />
  );
};

export default Flows;
